/**
 * "Database" tab of the runtime System administration console — where a
 * customer organization's functional administrator sees, and changes, the
 * database THEIR copy of the application runs in.
 *
 * Accepting the invitation puts the organization on a database the platform
 * provisions for it (`twasta_preview_<tenant>`), so the app works from the
 * first sign-in and nobody is asked for credentials to get in. This panel is
 * the opt-out for an organization that would rather keep its data in a
 * database it controls: the application's tables and business logic are
 * deployed into it, and from then on every read and write of theirs goes
 * there.
 *
 * Two things it is careful about, because both are irreversible-feeling:
 *   - data is NOT migrated. A switch starts empty; the old database keeps
 *     what was entered in it. Said before the button, not after.
 *   - the switch is proven before it is stored (the server deploys first), so
 *     a failed attempt leaves the organization running where it already was.
 *
 * Only offered to a func-admin OUTSIDE the owner's organization — the owner's
 * own database is the project's, changed in Database Designer.
 */
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Database, Loader2, RotateCcw, Save } from 'lucide-react'
import {
  getMyTenantDatabase, resetMyTenantDatabase, setMyTenantDatabase,
  type MyTenantDatabase, type TenantDatabaseBody,
} from '@/api/team'

const inp: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6,
  fontSize: 14, background: '#fff', color: '#1f2937', outline: 'none', width: '100%',
}
const btn = (primary?: boolean): React.CSSProperties => ({
  padding: '7px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  border: primary ? 'none' : '1px solid #cbd5e1',
  background: primary ? '#4f46e5' : '#fff', color: primary ? '#fff' : '#334155',
  display: 'inline-flex', alignItems: 'center', gap: 6,
})

const DB_LABEL: Record<string, string> = {
  postgres: 'PostgreSQL', oracle: 'Oracle', sqlite: 'SQLite',
}
const DEFAULT_PORT: Record<string, number> = { postgres: 5432, oracle: 1521 }

export function OrganizationDatabasePanel({ projectId }: { projectId: string }) {
  const [view, setView] = useState<MyTenantDatabase | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<'test' | 'save' | 'reset' | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)
  const [editing, setEditing] = useState(false)

  const [host, setHost] = useState('')
  const [port, setPort] = useState('')
  const [databaseName, setDatabaseName] = useState('')
  const [connectType, setConnectType] = useState('service_name')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [schema, setSchema] = useState('')
  const [sqlitePath, setSqlitePath] = useState('')

  const load = useCallback(() => {
    setLoading(true); setErr(null)
    getMyTenantDatabase(projectId)
      .then((v) => {
        setView(v)
        const c = v.connection
        setHost(c?.host || '')
        setPort(String(c?.port ?? DEFAULT_PORT[v.db_type] ?? ''))
        setDatabaseName(c?.database_name || '')
        setConnectType(c?.oracle_connect_type || 'service_name')
        setUsername(c?.username || '')
        setSchema(c?.db_schema || '')
        setSqlitePath(c?.sqlite_path || '')
        setPassword('')
        setEditing(!v.using_default)
      })
      .catch((e) => setErr(errText(e) || 'Could not read your database settings'))
      .finally(() => setLoading(false))
  }, [projectId])
  useEffect(() => { load() }, [load])

  if (loading) {
    return <div style={{ padding: 26, textAlign: 'center', color: '#64748b' }}><Loader2 className="animate-spin" /></div>
  }
  if (err && !view) return <div style={{ fontSize: 14, color: '#dc2626' }}>{err}</div>
  if (!view) return null

  const dbType = view.db_type
  const label = DB_LABEL[dbType] || dbType
  // The default database cannot run this application at all (an Oracle app on
  // the Postgres every organization is given). The organization is set up, but
  // the app's tables are not there and cannot be — supplying a database is the
  // only way forward, so the panel leads with that instead of an empty app.
  const mustSupply = view.using_default && view.db_type !== view.default_db_type

  const body = (testOnly: boolean): TenantDatabaseBody => ({
    db_type: dbType,
    host: host.trim() || undefined,
    port: port ? Number(port) : undefined,
    database_name: databaseName.trim() || undefined,
    oracle_connect_type: dbType === 'oracle' ? connectType : undefined,
    username: username.trim() || undefined,
    password: password || undefined,
    sqlite_path: sqlitePath.trim() || undefined,
    db_schema: schema.trim() || undefined,
    test_only: testOnly,
  })

  const submit = async (testOnly: boolean) => {
    if (!testOnly && !window.confirm(
      `Move this application into your own ${label} database?\n\n` +
      'Its tables and business logic are created there, and your organization ' +
      'starts working in it. Data already entered is NOT copied over — it stays ' +
      'in the database you are leaving.',
    )) return
    setBusy(testOnly ? 'test' : 'save'); setResult(null)
    try {
      const res = await setMyTenantDatabase(projectId, body(testOnly))
      if (res.success && !testOnly) {
        toast.success('Your organization now runs on your own database')
        load()
        return
      }
      setResult({
        ok: !!res.success,
        message: res.message || (res.success ? 'Connected' : 'Could not connect'),
      })
    } catch (e) {
      setResult({ ok: false, message: errText(e) || 'Could not save the database details' })
    } finally { setBusy(null) }
  }

  const reset = async () => {
    if (!window.confirm(
      'Go back to the database Twasta provides for your organization?\n\n' +
      'The application is re-deployed there and your organization starts ' +
      'working in it. Nothing in your own database is deleted — but the data ' +
      'entered there does not come with you.',
    )) return
    setBusy('reset'); setResult(null)
    try {
      await resetMyTenantDatabase(projectId)
      toast.success('Back on the database provided for your organization')
      load()
    } catch (e) {
      const msg = errText(e) || 'Could not switch back'
      setResult({ ok: false, message: msg }); toast.error(msg)
    } finally { setBusy(null) }
  }

  const field = (
    text: string, value: string, onChange: (v: string) => void,
    opts: { type?: string; placeholder?: string; hint?: string } = {},
  ) => (
    <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
      {text}
      <input
        style={inp} type={opts.type || 'text'} value={value}
        placeholder={opts.placeholder} autoComplete={opts.type === 'password' ? 'new-password' : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {opts.hint && <span style={{ fontSize: 13, color: '#64748b' }}>{opts.hint}</span>}
    </label>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, color: '#1f2937' }}>
      <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
        The database <b>your organization's</b> data lives in. Every record your users
        enter is written here — never into the application owner's database.
      </p>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 9, fontSize: 14,
        background: mustSupply ? '#fffbeb' : view.using_default ? '#eff6ff' : '#f0fdf4',
        color: mustSupply ? '#92400e' : view.using_default ? '#1e40af' : '#166534',
        border: `1px solid ${mustSupply ? '#fde68a' : view.using_default ? '#bfdbfe' : '#bbf7d0'}`,
        borderRadius: 8, padding: '9px 11px',
      }}>
        <Database size={15} style={{ flexShrink: 0 }} />
        <span>
          {mustSupply
            ? <>This application runs on <b>{label}</b>, which the database Twasta provides
              cannot be. Your organization is set up, but the application's tables are not —
              enter a {label} database of your own below to finish.</>
            : view.using_default
              ? <>Running on the database <b>Twasta provides</b> for your organization{view.db_name ? <> (<code>{view.db_name}</code>)</> : null}. Nothing to set up.</>
              : <>Running on <b>your own {label} database</b>{view.db_name ? <> (<code>{view.db_name}</code>)</> : null}.</>}
        </span>
      </div>

      {view.using_default && !editing && !mustSupply && (
        <div>
          <button type="button" style={btn()} onClick={() => setEditing(true)}>
            <Database size={14} /> Use my organization's own database
          </button>
        </div>
      )}

      {(editing || mustSupply || !view.using_default) && (
        <>
          <div style={{
            fontSize: 13.5, background: '#fffbeb', color: '#92400e',
            border: '1px solid #fde68a', borderRadius: 8, padding: '9px 11px',
          }}>
            This application runs on <b>{label}</b>, so your database has to be {label} too —
            its business logic is written for it. The application's tables are created in the
            database you give here, so the account must be allowed to create them.
            <b> Data already entered is not copied across.</b>
          </div>

          {dbType === 'sqlite' ? (
            field('Database file path', sqlitePath, setSqlitePath, { placeholder: '/data/myorg.db' })
          ) : (
            <>
              {field('Host', host, setHost, { placeholder: 'db.myorg.com' })}
              {field('Port', port, setPort)}
              {dbType === 'oracle' && (
                <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
                  Connect using
                  <select style={inp} value={connectType} onChange={(e) => setConnectType(e.target.value)}>
                    <option value="service_name">Service name</option>
                    <option value="sid">SID</option>
                  </select>
                </label>
              )}
              {field(
                dbType === 'oracle' ? (connectType === 'sid' ? 'SID' : 'Service name') : 'Database name',
                databaseName, setDatabaseName,
              )}
              {field('Username', username, setUsername, {
                hint: dbType === 'oracle'
                  ? 'In Oracle a schema is a user — connect as the user that OWNS the schema below, or leave the schema blank to use this user\'s own.'
                  : undefined,
              })}
              {field('Password', password, setPassword, {
                type: 'password',
                placeholder: view.connection?.has_password ? '•••••••• stored — type to replace' : undefined,
              })}
              {field('Schema (optional)', schema, setSchema)}
            </>
          )}

          {result && (
            <div style={{ fontSize: 13.5, color: result.ok ? '#166534' : '#dc2626' }}>
              {result.message}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" style={btn()} disabled={!!busy} onClick={() => submit(true)}>
              {busy === 'test' ? <Loader2 size={14} className="animate-spin" /> : null} Test connection
            </button>
            <button type="button" style={btn(true)} disabled={!!busy} onClick={() => submit(false)}>
              {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {view.using_default ? ' Move the application here' : ' Save & redeploy'}
            </button>
            {!view.using_default && (
              <button type="button" style={btn()} disabled={!!busy} onClick={reset}>
                {busy === 'reset' ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                {' '}Use the database Twasta provides
              </button>
            )}
            {view.using_default && editing && (
              <button type="button" style={btn()} disabled={!!busy} onClick={() => { setEditing(false); setResult(null) }}>
                Cancel
              </button>
            )}
          </div>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>
            Saving creates the application's tables in that database. It can take a minute.
          </span>
        </>
      )}
    </div>
  )
}

function errText(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string') return detail
  return e instanceof Error ? e.message : ''
}
